/**
 * Loans Management API
 * Handles loans, renewals, fines, policies, history, and statistics
 */
import apiBase, { BaseQueryParams, PaginatedResponse, idempotencyHeaders } from './api-base';

// Shared basic types
export interface LoanPolicy {
  id: number;
  user_type: string;
  max_books: number;
  loan_period_days: number;
  max_renewals: number;
  fine_per_day: string;
  grace_period_days: number;
  created_at: string;
  updated_at: string;
}

export interface LoanUserInfo {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  user_type: string;
  student_id?: string;
  employee_id?: string;
}

export interface LoanBookCopyInfo {
  id: number;
  barcode: string;
  accession_number: string;
  book_title: string;
  book: number; // the catalogue id, so a loan can link to its book's page
  book_isbn: string;
  book_authors: string[]; // server provides StringRelatedField list
  book_cover_image?: string | null; // bare relative media path (use_url=False)
  status: string;
  location?: number;
  location_display?: string;
  condition?: number;
  condition_display?: string;
}

export interface LoanRenewal {
  id: number;
  loan: number;
  loan_info: string;
  renewal_date: string;
  previous_due_date: string;
  new_due_date: string;
  renewed_by: number;
  renewed_by_name: string;
  reason?: string;
  created_at: string;
}

export interface Fine {
  id: number;
  loan: number;
  loan_info: string;
  borrower_name: string;
  amount: string;
  amount_paid: string;
  outstanding_amount: string;
  is_fully_paid: boolean;
  reason?: string;
  description?: string;
  status: 'pending' | 'paid' | 'waived' | 'partially_paid';
  calculated_date: string;
  paid_date?: string;
  waived_date?: string;
  waived_by?: number;
  waived_by_name?: string;
  waiver_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface Loan {
  id: number;
  book_copy: number;
  book_copy_info?: LoanBookCopyInfo;
  borrower: number;
  borrower_info?: LoanUserInfo;
  loan_date: string;
  due_date: string;
  return_date?: string;
  renewed_count: number;
  status: 'active' | 'returned' | 'overdue' | 'lost';
  issued_by: number;
  issued_by_name?: string;
  notes?: string;
  is_overdue: boolean;
  days_overdue: number;
  can_renew_status: { can_renew: boolean; reason: string };
  current_fine: string;
  renewals?: LoanRenewal[];
  fines?: Fine[];
  created_at: string;
  updated_at: string;
}

export interface LoanHistoryRecord {
  id: number;
  original_loan_id: number;
  book_title: string;
  book_isbn: string;
  borrower_email: string;
  borrower_name: string;
  loan_date: string;
  due_date: string;
  return_date?: string;
  renewed_count: number;
  total_fine_amount: string;
  created_at: string;
}

// Borrow Request types
export interface BorrowRequest {
  id: number;
  book: number;
  book_title?: string;
  borrower: number;
  borrower_name?: string;
  borrower_username?: string;
  borrower_email?: string;
  borrower_user_type?: string;
  status: 'pending' | 'fulfilled' | 'rejected' | 'cancelled';
  created_at: string;
  processed_at?: string;
  selected_copy?: number;
  approved_by?: number;
  approved_by_name?: string;
  rejection_reason?: string;
  notes?: string;
  resulting_loan?: number;
}

// Statistics / Overview types
export interface LoanStatisticsSummary {
  total_active_loans: number;
  total_overdue_loans: number;
  total_loans_today: number;
  total_returns_today: number;
  total_renewals_today: number;
  total_outstanding_fines: string;
  average_loan_duration: number;
  most_borrowed_books?: any[]; // backend returns list structures; keep generic
  top_borrowers?: any[];
}

export interface LoansOverview {
  summary: Record<string, any>;
  recent_loans: Loan[];
  overdue_loans: Loan[];
  pending_fines: Fine[];
  policy_summary: Array<{
    user_type: string;
    max_books: number;
    loan_period_days: number;
    max_renewals: number;
  }>;
}

// Query params
export interface LoanQueryParams extends BaseQueryParams {
  status?: string;
  borrower?: number;
  book_copy?: number;
  book?: number;              // filter by book id
  overdue?: boolean | string; // API expects 'true'
  start_date?: string;
  end_date?: string;
  search?: string;
}

export interface FineQueryParams extends BaseQueryParams {
  status?: string;
  borrower?: number;
  outstanding?: boolean | string;
}

export interface RenewalQueryParams extends BaseQueryParams {
  loan?: number;
  borrower?: number;
}

export interface HistoryQueryParams extends BaseQueryParams {
  borrower_email?: string;
  book_title?: string;
  start_date?: string;
  end_date?: string;
}

// Create / Update payloads
/** GET loans/loans/eligibility/?borrower=: the same answer the desk gives when issuing. */
export interface BorrowerEligibility {
  eligible: boolean;
  reasons: string[];
  open_loans: number;
  max_books: number | null;
  outstanding_fines: string;
}

export interface CreateLoanData {
  book_copy: number;
  borrower: number;
  /** ISO datetime. Left out, the borrower's loan policy sets it. */
  due_date?: string;
  issued_by?: number; // server overrides with current user
  notes?: string;
}

export interface UpdateLoanData extends Partial<CreateLoanData> {
  id?: never;
  status?: Loan['status'];
  return_date?: string;
}

export interface CreateFineData {
  loan: number;
  amount: string;
  reason?: string;
  description?: string;
}

export interface UpdateFineData extends Partial<CreateFineData> {
  id?: never;
  status?: Fine['status'];
  amount_paid?: string;
}

export interface CreateLoanPolicyData {
  user_type: string;
  max_books: number;
  loan_period_days: number;
  max_renewals: number;
  fine_per_day: string;
  grace_period_days: number;
}

export interface UpdateLoanPolicyData extends Partial<CreateLoanPolicyData> {
  id?: never;
}

// Loan APIs
export const loansApi = {
  getAll: (params?: LoanQueryParams) => apiBase.get<PaginatedResponse<Loan>>('/loans/loans/', { params }),
  getById: (id: number) => apiBase.get<Loan>(`/loans/loans/${id}/`),
  // Desk actions carry an Idempotency-Key so a retried submit never issues, renews or returns twice.
  create: (data: CreateLoanData, idempotencyKey?: string) => apiBase.post<{ message: string; loan: Loan }>('/loans/loans/', data, { headers: idempotencyHeaders(idempotencyKey) }),
  update: (id: number, data: UpdateLoanData) => apiBase.patch<Loan>(`/loans/loans/${id}/`, data),
  renew: (id: number, payload: { reason?: string; new_due_date?: string }, idempotencyKey?: string) => apiBase.post<{ message: string; renewal: LoanRenewal; loan: Loan }>(`/loans/loans/${id}/renew/`, payload, { headers: idempotencyHeaders(idempotencyKey) }),
  returnBook: (id: number, payload?: { notes?: string; condition_id?: number }, idempotencyKey?: string) => apiBase.post<{ message: string; fine_amount: string; loan: Loan }>(`/loans/loans/${id}/return_book/`, payload || {}, { headers: idempotencyHeaders(idempotencyKey) }),
  eligibility: (borrowerId: number) => apiBase.get<BorrowerEligibility>('/loans/loans/eligibility/', { params: { borrower: borrowerId } }),
  canRenew: (id: number) => apiBase.get<{ can_renew: boolean; reason: string; current_renewals: number }>(`/loans/loans/${id}/can_renew/`),
  calculateFine: (id: number) => apiBase.get<{ fine_amount: string; is_overdue: boolean; days_overdue: number }>(`/loans/loans/${id}/calculate_fine/`),
  getOverdue: (params?: { page?: number; page_size?: number }) => apiBase.get<PaginatedResponse<Loan>>(`/loans/loans/overdue/`, { params }),
  getActive: (params?: { page?: number; page_size?: number }) => apiBase.get<PaginatedResponse<Loan>>(`/loans/loans/active/`, { params }),
  getByBorrower: (borrower_id: number) => apiBase.get<PaginatedResponse<Loan> & { borrower_id: number }>(`/loans/loans/by_borrower/?borrower_id=${borrower_id}`),
  getMySummary: () => apiBase.get<{ loans: Loan[]; active_count: number; outstanding_fines_count: number; outstanding_fines_total: string }>(`/loans/loans/my/summary/`),
  // Borrow request workflow
  createBorrowRequest: (payload: { book: number; notes?: string }) => apiBase.post<{ message: string; request: BorrowRequest }>(`/loans/loans/request/`, payload),
  getMyBorrowRequests: (params?: { page?: number; page_size?: number }) => apiBase.get<PaginatedResponse<BorrowRequest>>(`/loans/loans/my/requests/`, { params }),
  getPendingBorrowRequests: (params?: { page?: number; page_size?: number }) => apiBase.get<PaginatedResponse<BorrowRequest>>(`/loans/loans/requests/pending/`, { params }),
  approveBorrowRequest: (requestId: number) => apiBase.post<{ message: string; loan: Loan; request: BorrowRequest }>(`/loans/loans/requests/${requestId}/approve/`, {}),
  rejectBorrowRequest: (requestId: number, reason: string) => apiBase.post<{ message: string; request: BorrowRequest }>(`/loans/loans/requests/${requestId}/reject/`, { reason }),
  cancelBorrowRequest: (requestId: number) => apiBase.post<{ message: string; request: BorrowRequest }>(`/loans/loans/requests/${requestId}/cancel/`, {}),
};

// Fine APIs
export const finesApi = {
  getAll: (params?: FineQueryParams) => apiBase.get<PaginatedResponse<Fine>>('/loans/fines/', { params }),
  getById: (id: number) => apiBase.get<Fine>(`/loans/fines/${id}/`),
  create: (data: CreateFineData) => apiBase.post<Fine>('/loans/fines/', data),
  update: (id: number, data: UpdateFineData) => apiBase.patch<Fine>(`/loans/fines/${id}/`, data),
  // Librarian-only; retries carry the same Idempotency-Key so a payment is never recorded twice.
  pay: (id: number, payload: { amount: string; payment_method?: string; notes?: string }, idempotencyKey?: string) => apiBase.post<{ message: string; amount_paid: string; outstanding_amount: string; is_fully_paid: boolean; fine: Fine }>(`/loans/fines/${id}/pay/`, payload, { headers: idempotencyHeaders(idempotencyKey) }),
  waive: (id: number, payload: { reason: string }) => apiBase.post<{ message: string; waiver_reason: string; fine: Fine }>(`/loans/fines/${id}/waive/`, payload),
  getOutstanding: (params?: { page?: number; page_size?: number }) => apiBase.get<PaginatedResponse<Fine> & { total_outstanding: string }>(`/loans/fines/outstanding/`, { params }),
};

// Renewals APIs (read-only)
export const renewalsApi = {
  getAll: (params?: RenewalQueryParams) => apiBase.get<PaginatedResponse<LoanRenewal>>('/loans/renewals/', { params }),
  getById: (id: number) => apiBase.get<LoanRenewal>(`/loans/renewals/${id}/`),
};

// Policies APIs
export const loanPoliciesApi = {
  getAll: (params?: { user_type?: string }) => apiBase.get<PaginatedResponse<LoanPolicy>>('/loans/policies/', { params }),
  getById: (id: number) => apiBase.get<LoanPolicy>(`/loans/policies/${id}/`),
  create: (data: CreateLoanPolicyData) => apiBase.post<LoanPolicy>('/loans/policies/', data),
  update: (id: number, data: UpdateLoanPolicyData) => apiBase.patch<LoanPolicy>(`/loans/policies/${id}/`, data),
};

// History APIs (read-only)
export const loanHistoryApi = {
  getAll: (params?: HistoryQueryParams) => apiBase.get<PaginatedResponse<LoanHistoryRecord>>('/loans/history/', { params }),
  getById: (id: number) => apiBase.get<LoanHistoryRecord>(`/loans/history/${id}/`),
};

// Statistics / Overview APIs
export const loanStatsApi = {
  getSummary: () => apiBase.get<LoanStatisticsSummary>('/loans/statistics/'),
};

// Combined export
export const loansApiGroup = {
  loans: loansApi,
  fines: finesApi,
  renewals: renewalsApi,
  policies: loanPoliciesApi,
  history: loanHistoryApi,
  statistics: loanStatsApi,
};

export default loansApiGroup;
